import { useEffect, useState } from 'react';
import { olHeaders as headers } from './olHeaders';

const SUPPORT_EMAIL_DOMAIN = '@climatecorp.support';
const SUPPORT_PROXY_DATA_SOURCE_KEY = 'field-view.support-proxy-data-source';
export const SUPPORT_PROXY_DATA_SOURCE_CHANGED_EVENT = 'field-view.support-proxy-data-source.changed';

type SharedDataUser = {
  id?: number;
  details?: {
    id?: number;
    email?: string;
    countryCode?: string;
  };
};

type SharedDataResolvedUser = {
  user?: SharedDataUser;
  loggedInUser?: SharedDataUser;
  impersonatedUser?: SharedDataUser;
  isProxying?: boolean;
};

export const isSupportUser = (email?: string): boolean =>
  Boolean(email && email.toLowerCase().endsWith(SUPPORT_EMAIL_DOMAIN));

export const isSupportProxySession = (user: SharedDataUser | undefined, isProxying: boolean): boolean => {
  const userEmail = user?.details?.email;

  return Boolean(
    isProxying
    && isSupportUser(userEmail),
  );
};

export const shouldUseProxyDataSource = (): boolean => {
  try {
    const storedValue = sessionStorage.getItem(SUPPORT_PROXY_DATA_SOURCE_KEY);
    if (storedValue === null) {
      return true;
    }

    return storedValue === 'true';
  } catch {
    return true;
  }
};

export const setShouldUseProxyDataSource = (shouldUseProxyData: boolean): void => {
  try {
    sessionStorage.setItem(SUPPORT_PROXY_DATA_SOURCE_KEY, String(shouldUseProxyData));
  } catch {
    // Ignore storage issues and keep app behavior based on defaults.
  }

  // Notify other mounted plugins (dashboard, etc.) to refresh hook-driven data.
  window.dispatchEvent(new CustomEvent(SUPPORT_PROXY_DATA_SOURCE_CHANGED_EVENT, {
    detail: { shouldUseProxyData },
  }));
};

export const subscribeToSupportProxyDataSourceChanges = (
  onChange: () => void,
): (() => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key === SUPPORT_PROXY_DATA_SOURCE_KEY) {
      onChange();
    }
  };

  window.addEventListener(SUPPORT_PROXY_DATA_SOURCE_CHANGED_EVENT, onChange);
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener(SUPPORT_PROXY_DATA_SOURCE_CHANGED_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
};

export const useShouldUseProxyDataSourcePreference = (): boolean => {
  const [shouldUseProxyData, setShouldUseProxyData] = useState<boolean>(
    () => shouldUseProxyDataSource(),
  );

  useEffect(
    () => subscribeToSupportProxyDataSourceChanges(() => {
      setShouldUseProxyData(shouldUseProxyDataSource());
    }),
    [],
  );

  return shouldUseProxyData;
};

export const selectUserByProxyPreference = (
  resolvedUser: SharedDataResolvedUser,
  shouldUseProxyData = shouldUseProxyDataSource(),
): SharedDataUser | undefined => {
  const {
    user,
    loggedInUser,
    impersonatedUser,
    isProxying,
  } = resolvedUser;

  const baseLoggedInUser = loggedInUser || user;
  if (!baseLoggedInUser) {
    return user;
  }

  if (!isProxying) {
    return baseLoggedInUser;
  }

  const proxiedUser = impersonatedUser || user || baseLoggedInUser;

  if (!isSupportUser(baseLoggedInUser.details?.email)) {
    return proxiedUser;
  }

  return shouldUseProxyData ? proxiedUser : baseLoggedInUser;
};

export const useUserLoggedInConfigs = {
  config: {
    headers,
    skip: false
  }
};

export const useFeatureFlagsConfigs = (selectedUser?: SharedDataUser) => {
  if (!selectedUser) {
    return {
      config: {
        headers,
        skip: false,
      },
    };
  }

  const legacyUserId = (selectedUser.id ?? selectedUser.details?.id)?.toString() || '';
  const countryCode = selectedUser.details?.countryCode || '';

  return {
    params: {
      legacyUserId,
      countryCode,
    },
    config: {
      headers,
      skip: !legacyUserId || !countryCode,
    },
  };
};

export const useProductAssetsByUserConfigs = (selectedUser?: SharedDataUser) => {
  if (!selectedUser) {
    return {
      config: {
        headers,
        skip: false,
      },
    };
  }

  const userId = selectedUser.id ?? selectedUser.details?.id;
  const config = {
    headers,
    skip: !userId,
  };

  if (!userId) {
    return { config };
  }

  return {
    params: {
      userId,
    },
    config,
  };
};

export const withUseQueryConfig = (hookArgs: { config?: Record<string, unknown>;[key: string]: unknown } = {}) => ({
  ...hookArgs,
  config: {
    ...(hookArgs.config || {}),
    useQuery: true,
  },
});
